import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { MOCK_PATIENTS } from "../data/mockData";
import { logAudit } from "../utils/auditLog";
import { supabase } from "../lib/supabase";
import { getCurrentProvider } from "../lib/currentProvider";
import { NOTIFY_EVENT, _setNotifyProviderMounted } from "../utils/notify";
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

export const AppContext = createContext(null);

const DEMO_PRACTICE_ID = '00000000-0000-0000-0000-000000000001';

export function AppProvider({ children, session, practiceId: practiceIdProp }) {
  const practiceId = isDemoMode ? DEMO_PRACTICE_ID : practiceIdProp;
  const [patients, setPatients] = useState(isDemoMode ? MOCK_PATIENTS : []);
  const [activePatient, setActivePatient] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [navParams, setNavParams] = useState({});
  const [notification, setNotification] = useState(null);
  const [chartInitialTab, setChartInitialTab] = useState("summary");
  const [dialerPatient, setDialerPatient] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [smsPatient, setSmsPatient] = useState(null);

  // ── Current provider row + RBAC role (both from auth_user_id) ───────────────
  const [currentProvider, setCurrentProvider] = useState(null);
  const [userRole, setUserRole] = useState(undefined); // undefined = not yet loaded (fail-open)
  useEffect(() => {
    if (isDemoMode) { setCurrentProvider(null); setUserRole('admin'); return; }
    if (!session?.user?.id) { setCurrentProvider(null); return; }
    getCurrentProvider().then(cp => {
      setCurrentProvider(cp);
      setUserRole(cp?.role || 'provider');
    });
  }, [session?.user?.id]); // eslint-disable-line

  const showNotification = useCallback((msg, type = "info") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Bridge for utils/notify.js — lets deeply nested components fire toasts
  // without prop-threading (the alert() → toast pass, 2026-07-08).
  useEffect(() => {
    _setNotifyProviderMounted(true);
    const handler = (e) => showNotification(e.detail?.msg || '', e.detail?.type || 'info');
    window.addEventListener(NOTIFY_EVENT, handler);
    return () => {
      _setNotifyProviderMounted(false);
      window.removeEventListener(NOTIFY_EVENT, handler);
    };
  }, [showNotification]);

  const handlePatientSelect = useCallback((patient, initialTab = "summary") => {
    setActivePatient(patient);
    setChartInitialTab(initialTab);
    setPage("chart");
    logAudit('view', 'patient', patient.id, patient.id, { name: `${patient.first || ''} ${patient.last || ''}`.trim() });
  }, []);

  const handleNavigate = useCallback((dest, params = {}) => {
    if (dest === "new-patient") { setPage("new-patient"); setActivePatient(null); }
    else { setPage(dest); setActivePatient(null); }
    setNavParams(params);
  }, []);

  const handleSavePatient = useCallback((form) => {
    const newPt = { ...form, id: `P${Date.now()}`, mrn: `OC-${10050 + patients.length}`, allergies: form.allergies ? form.allergies.split(",").map(s => s.trim()) : [], problems: [], lastVisit: null, nextAppt: null, copay: Number(form.copay) || 0 };
    setPatients(p => [...p, newPt]);
    showNotification(`${form.first} ${form.last} registered successfully`);
    setPage("patients");
  }, [patients, showNotification]);

  const handleUpdatePatient = useCallback((updated) => {
    setPatients(p => p.map(pt => pt.id === updated.id ? updated : pt));
    setActivePatient(updated);
  }, []);

  return (
    <AppContext.Provider value={{
      patients, setPatients,
      activePatient, setActivePatient,
      page, setPage,
      navParams,
      notification,
      chartInitialTab,
      dialerPatient, setDialerPatient,
      incomingCall, setIncomingCall,
      smsPatient, setSmsPatient,
      session, practiceId,
      userRole,
      currentProvider,
      showNotification,
      handlePatientSelect,
      handleNavigate,
      handleSavePatient,
      handleUpdatePatient,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
